/**
 * Pricing Page — Central Handoff
 *
 * BeHeard does NOT own billing. Plan selection and subscription changes
 * are managed by Central (LevelUp).
 *
 * This page shows available plans (read-only) and sends users to Central
 * to complete any billing action.
 *
 * Route: /pricing (public)
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PRICING_PLANS, type PlanTier } from '../config/pricingPlans';
import { LIMITS_FOOTNOTE } from '../config/planLimitCopy';
import { useCentralBillingLinks } from '../hooks/useCentralBillingLinks';
import { openCentralBilling } from '../services/centralBillingUrls';
import { showAskOwnerToast, showBillingFallbackToast } from '../components/billing/billingToasts';

export function PricingPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { canManage, bootReady, urlFor } = useCentralBillingLinks();

  // The commercial plan is Central's (docs/PLAN-AUTHORITY.md). The Central
  // user carries no plan here, so no card is claimed as "Your plan" when it is
  // unknown — the old `|| 'free'` labelled every paying user's plan as Free.
  const currentPlanTier: PlanTier | null = (user?.planTier as PlanTier | undefined) ?? null;

  // Suite contract 1.2: plan changes happen on Central's plans page
  // (`/workspace/<id>/settings/billing?view=plans&app=beheard&return_to=…`).
  // The old hand-off asked BeHeard's backend for a Stripe portal and, on any
  // failure, opened `${CENTRAL_URL}/billing` — a path Central does not serve.
  const handleManageBilling = () => {
    if (!isAuthenticated) {
      navigate('/login?returnTo=/pricing');
      return;
    }
    // /pricing is a public route with no BootGate, so a fresh sign-in can land
    // here before Central's switch-context has said who may manage billing.
    // Unknown is not "member": hand off to the in-app billing tab, which boots.
    if (!bootReady) {
      navigate('/settings/billing');
      return;
    }
    if (!canManage) {
      showAskOwnerToast();
      return;
    }
    const plansUrl = urlFor('plans');
    if (!plansUrl) {
      showBillingFallbackToast(null);
      return;
    }
    openCentralBilling(plansUrl);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Choose Your Plan</h1>
        <p className="mt-2 text-lg text-gray-500">
          Select the perfect plan for your communication journey
        </p>
      </div>

      {/* Not logged in banner */}
      {!isAuthenticated && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-blue-800">
            <span className="font-medium">Ready to get started?</span>{' '}
            <button
              onClick={() => navigate('/login')}
              className="underline hover:no-underline"
            >
              Sign up or log in
            </button>{' '}
            to choose a plan.
          </p>
        </div>
      )}

      {/* Plan cards (read-only display) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {PRICING_PLANS.map((plan) => (
          <div
            key={plan.tier}
            className={`card relative ${
              plan.isPopular ? 'ring-2 ring-primary-500 shadow-lg' : ''
            } ${plan.tier === currentPlanTier ? 'bg-primary-50' : ''}`}
          >
            {plan.isPopular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  MOST POPULAR
                </span>
              </div>
            )}

            {plan.tier === currentPlanTier && isAuthenticated && (
              <div className="absolute -top-3 right-4">
                <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  YOUR PLAN
                </span>
              </div>
            )}

            <div className="card-body space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900">{plan.name}</h2>
                <p className="text-gray-500 text-sm mt-1">{plan.headline}</p>
                {/* No local prices: the price is Central's, shown at checkout in LevelUp. */}
                <p className="mt-4 text-sm text-gray-500" data-testid="pricing-price-in-levelup">
                  Pricing shown in LevelUp
                </p>
              </div>

              <p className="text-sm text-gray-600 text-center">{plan.description}</p>

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium uppercase">Best for</p>
                <p className="text-sm text-gray-700">{plan.bestFor}</p>
              </div>

              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <svg
                      className="w-5 h-5 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Single CTA: manage in Central */}
              <button
                onClick={handleManageBilling}
                disabled={plan.tier === currentPlanTier && isAuthenticated}
                className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  plan.tier === currentPlanTier && isAuthenticated
                    ? 'btn-secondary opacity-50 cursor-not-allowed'
                    : plan.isPopular
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                {plan.tier === currentPlanTier && isAuthenticated
                  ? 'Current Plan'
                  : isAuthenticated && bootReady && !canManage
                  ? 'Ask your workspace owner'
                  : 'Manage in LevelUp'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-sm text-gray-500 text-center" data-testid="pricing-limits-footnote">
        {LIMITS_FOOTNOTE}
      </p>
    </div>
  );
}
